"use client";

import MediaTextCard, { MediaTextCardTextSlotProps } from "@/components/common/media-text-card/media-text-card";
import { MediaCard } from "@/types/dto/common/media-card-section";
import { Box } from "@mui/system";
import React from "react";
import { BasicInfo } from "@/types/dto/common/basic-info";
import { SectionHeader } from "@/components/common/section-header";
import { SxProps } from "@mui/material";
import { deepmerge } from "@mui/utils";

interface MediaCardSectionSlotsProps {
  root?: {
    className?: string;
    sx?: SxProps;
  };
  sectionHeader?: {
    container?: {
      sx?: SxProps;
    }
    header?: {
      sx?: SxProps;
      testId?: string;
    };
    copy?: {
      sx?: SxProps;
      testId?: string;
    };
  }
  mediaCards?: MediaTextCardTextSlotProps;
}

export interface MediaCardsSectionProps {
  mediaCards: MediaCard[];
  moduleRankingPosition: number;
  slotProps?: MediaCardSectionSlotsProps;
  basicInfo?: BasicInfo;
  showImageText?: boolean;
  reverseMobile?: boolean;
  useCircularPlayButton?: boolean;
  bgImage?: string;
}

const MediaCardsSection = ({
  mediaCards,
  moduleRankingPosition,
  slotProps,
  basicInfo,
  showImageText = true,
  reverseMobile,
  useCircularPlayButton = false,
  bgImage
}: MediaCardsSectionProps) => (
  <Box
    component="section"
    data-module-name="Media Cards"
    data-parent-container-type="media-cards-section"
    data-ranking-algorithm="manual"
    data-module-ranking-position={moduleRankingPosition}
    data-track="visibility"
    data-testid="media-cards-section"
    className={slotProps?.root?.className || ""}
    sx={
      deepmerge({
        padding: { xs: "4rem 2.4rem 0", sm: "6rem 2.4rem 0" },
        "& > *": { // allows pattern-bg class
          position: "relative",
          zIndex: 2
        }
      },
        {
          ...slotProps?.root?.sx
        })
    }
  >
    {basicInfo && (
      <SectionHeader
        headerText={basicInfo.headerText}
        copyText={basicInfo.copyText}
        headerTestId={slotProps?.sectionHeader?.header?.testId}
        headerSx={deepmerge({
          textAlign: "center",
          textTransform: "none"
        },
          { ...slotProps?.sectionHeader?.header?.sx })
        }
        copySx={deepmerge({
          textAlign: "center"
        },
          { ...slotProps?.sectionHeader?.copy?.sx })
        }
        copyTestId={slotProps?.sectionHeader?.copy?.testId}
        sx={deepmerge({
          textAlign: "center",
          justifyContent: "center",
          width: "100%",
          "& p": {
            margin: 0
          },
          gap: "2.4rem"
        }, {
          ...slotProps?.sectionHeader?.container?.sx
        })}
      />
    )}
    {mediaCards?.map((card: MediaCard, index: number) => (
      <MediaTextCard
        key={card?.mediaCardId || index}
        testId={index}
        index={index}
        reverseDesktop={card?.design === "Text Left"}
        reverseMobile={reverseMobile ?? card?.design === "Text Left"}
        card={card}
        slotProps={slotProps?.mediaCards}
        showImageText={showImageText}
        useCircularPlayButton={useCircularPlayButton}
        bgImage={bgImage}
      />
    ))}
  </Box>
);

export default MediaCardsSection;
